/**
 * What a heritage surface may say about a record's notes (TEM-1346, TEM-1568).
 *
 * ONE FILE AT FOUR PATHS, BYTE-IDENTICAL:
 *
 *   apps/record/src/services/narrativeState.ts        desktop
 *   apps/record/web/src/lineage/narrative.ts          web library
 *   apps/dj/src/lib/narrativeState.ts                 DJ heritage rail
 *   apps/radio/frontend/src/utils/narrativeState.ts   Radio stream page
 *
 * The four apps are separate packages that cannot import one another, so the
 * rule is held by a test instead of a module boundary:
 * `apps/record/src/services/__tests__/narrativeState.test.ts` fails if any
 * copy differs from the desktop one by a byte. Edit the desktop copy, then run
 * `python3 scripts/audit/check_parity_copies.py --fix` (TEM-1727): the group is
 * declared in `scripts/audit/parity_copies.json` and CI's `lint-repo-hygiene`
 * fails on any drift.
 *
 * `lineage_summaries.narrative_text` is ONE text column carrying three kinds of
 * content: prose; '' (a row the refresh workflow wrote before any note was, with
 * generated_at 0); or a MARKER that `write_narrative_failure_activity` stores in
 * place of prose when generation fails
 * (`temporal_ml/activities/lineage_refresh.py`):
 *
 *     ⚠️ Narrative generation failed: unrecognized_entities:["Lee Perry"]
 *
 * Nothing upstream tells them apart. The server returns the column verbatim,
 * `GET /api/lineage/:id` calls a failure row `status: "ready"`, and the DJ
 * upload forwards it to Radio unfiltered. So every surface filters here.
 *
 * And no surface may say notes are "being written" unless something is writing
 * them. The server's `status: "generating"` means "edges exist and no summary
 * row does", not "a run is in flight"; ~9,720 albums sit in it while the bulk
 * queue has processed 7 albums ever and the web has no trigger at all. Only a
 * caller holding a `lineage_refresh_status` phase knows a run is in flight.
 */

/** The prefix stored in place of prose when generation failed. Byte-identical
 *  to `NARRATIVE_FAILURE_MARKER` in `temporal_ml/activities/lineage_refresh.py`
 *  (the guard test asserts it): a second spelling is how a filter like this
 *  silently stops matching. */
export const NARRATIVE_FAILURE_MARKER = '⚠️ Narrative generation failed';

/** True when a stored narrative is a failure marker rather than prose. The
 *  discriminator is the PREFIX: prose about a failed session is prose.
 *
 *  Both functions below check `typeof` rather than truthiness because Radio
 *  reads this field out of a schemaless DynamoDB passthrough: a number there
 *  must read as "no prose", not throw on the render path. */
export function isFailureNarrative(text: string | null | undefined): boolean {
  if (typeof text !== 'string') return false;
  return text.startsWith(NARRATIVE_FAILURE_MARKER);
}

/** The stored narrative IF it is fit to print, else null. Every surface that
 *  prints heritage prose goes through this: the marker is an internal
 *  diagnostic, and '' is not a note. */
export function narrativeProse(text: string | null | undefined): string | null {
  if (typeof text !== 'string' || !text.trim() || isFailureNarrative(text)) return null;
  return text;
}

/** The `lineage_refresh_status` phases in which a round is actually running.
 *  `idle` is what the status reports for an album never refreshed; `done` is a
 *  finished round. A round with no prose on file always reaches narrative
 *  generation (`existing_narrative_is_empty` in refresh_album_lineage.py counts
 *  '' and the marker), so both running phases mean notes are being written. */
const IN_FLIGHT_PHASES: ReadonlySet<string> = new Set(['sources_in_flight', 'narrative_gen']);

export function isRefreshInFlight(phase: string | null | undefined): boolean {
  return phase != null && IN_FLIGHT_PHASES.has(phase);
}

/** Which of five things a record's heritage notes can be.
 *
 *  - `ready`     prose is on file;
 *  - `writing`   a refresh round is in flight (only a caller that polls
 *                `lineage_refresh_status` can know this);
 *  - `failed`    the last attempt stored the failure marker;
 *  - `unwritten` no prose, nothing running: the notes are not written yet;
 *  - `none`      the record is KNOWN to have no connections. */
export type NarrativeState = 'ready' | 'writing' | 'failed' | 'unwritten' | 'none';

/**
 * @param connectionCount the record's counted edges. `null`/`undefined` means
 *   the caller does not know, and then "no connections" is never claimed.
 * @param inFlight true only while `lineage_refresh_status` reports a running
 *   phase ({@link isRefreshInFlight}) for this record.
 */
export function narrativeState(
  text: string | null | undefined,
  connectionCount: number | null | undefined,
  inFlight = false,
): NarrativeState {
  if (narrativeProse(text) !== null) return 'ready';
  // A round retries a failure, so while one runs the truthful sentence is
  // that notes are being written.
  if (inFlight) return 'writing';
  if (isFailureNarrative(text)) return 'failed';
  if (connectionCount == null || connectionCount > 0) return 'unwritten';
  return 'none';
}

/** One sentence per no-prose state, written once for every shell. None of
 *  them promises a retry: the web, DJ and Radio have no refresh to keep it. */
export const NARRATIVE_STATE_TEXT: Record<Exclude<NarrativeState, 'ready'>, string> = {
  writing: 'Heritage notes are being written for this record.',
  failed: 'Heritage notes could not be written for this record.',
  unwritten: 'Heritage notes have not been written for this record yet.',
  none: 'No heritage connections for this record yet.',
};
